import type { PrayerDay } from "./types";

export type PrayerKey = "fajr" | "dhuhr" | "asr" | "maghrib" | "isha";

export type NextPrayerInfo = {
  nowISO: string;
  current?: { key: PrayerKey; label: string };
  next: { key: PrayerKey; label: string; atLocal: string; inSeconds: number; dateGregorianISO: string };
};

const LABELS: Record<PrayerKey, string> = {
  fajr: "Fajr",
  dhuhr: "Dhuhr",
  asr: "Asr",
  maghrib: "Maghrib",
  isha: "Isha"
};

function partsInTimeZone(timeZone: string) {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  const parts = dtf.formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
    second: Number(get("second"))
  };
}

function zonedNowEpochMs(timeZone: string): number {
  const p = partsInTimeZone(timeZone);
  // Represent wall-clock time in the requested TZ as a UTC epoch for stable comparisons.
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

function parseHHMM(s: string): { hh: number; mm: number } | null {
  const m = /^\s*(\d{1,2}):(\d{2})/.exec(String(s));
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return { hh, mm };
}

function isoToYMD(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split("-").map((x) => Number(x));
  return { y, m, d };
}

function timeEpochMsForISODate(timeZone: string, dateISO: string, hhmm: string): number | null {
  const t = parseHHMM(hhmm);
  if (!t) return null;
  const { y, m, d } = isoToYMD(dateISO);
  // Same technique: wall-clock time -> UTC epoch.
  return Date.UTC(y, m - 1, d, t.hh, t.mm, 0);
}

function fmtHHMM(hhmm: string): string {
  const t = parseHHMM(hhmm);
  if (!t) return hhmm;
  return `${String(t.hh).padStart(2, "0")}:${String(t.mm).padStart(2, "0")}`;
}

export function computeNextPrayer(params: {
  day: PrayerDay;
  nextDay?: PrayerDay | null;
  timeZone: string;
}): NextPrayerInfo | null {
  const { day, nextDay, timeZone } = params;
  const now = zonedNowEpochMs(timeZone);

  const schedule: Array<{ key: PrayerKey; dateISO: string; at: string; epoch: number }> = [];
  (Object.keys(LABELS) as PrayerKey[]).forEach((k) => {
    const at = (day.timings as any)[k] as string;
    const epoch = timeEpochMsForISODate(timeZone, day.dateGregorianISO, at);
    if (epoch != null) schedule.push({ key: k, dateISO: day.dateGregorianISO, at, epoch });
  });

  // Add tomorrow's Fajr if available for post-Isha period.
  if (nextDay) {
    const at = nextDay.timings.fajr;
    const epoch = timeEpochMsForISODate(timeZone, nextDay.dateGregorianISO, at);
    if (epoch != null) schedule.push({ key: "fajr", dateISO: nextDay.dateGregorianISO, at, epoch });
  }

  schedule.sort((a, b) => a.epoch - b.epoch);
  if (!schedule.length) return null;

  let next = schedule.find((x) => x.epoch > now);
  if (!next) {
    // Past all prayers and no nextDay data (e.g. last day of month after Isha).
    // Estimate tomorrow's Fajr by adding ~24h to today's Fajr.
    const todayFajr = schedule.find((x) => x.key === "fajr");
    if (todayFajr) {
      const MS_DAY = 24 * 60 * 60 * 1000;
      next = { ...todayFajr, epoch: todayFajr.epoch + MS_DAY };
    } else {
      next = schedule[0];
    }
  }

  // Determine current prayer window (between this prayer time and the next prayer time).
  const todays = schedule.filter((x) => x.dateISO === day.dateGregorianISO);
  const nextInSchedule = schedule.find((x) => x.epoch > now);
  let current: NextPrayerInfo["current"] | undefined;
  if (nextInSchedule) {
    const before = [...todays].reverse().find((x) => x.epoch <= now);
    if (before) current = { key: before.key, label: LABELS[before.key] };
  } else {
    // After Isha (or after last prayer). Consider current as Isha if it already passed today.
    const ishaEpoch = todays.find((x) => x.key === "isha")?.epoch;
    if (ishaEpoch != null && now >= ishaEpoch) current = { key: "isha", label: "Isha" };
  }

  return {
    nowISO: new Date().toISOString(),
    current,
    next: {
      key: next.key,
      label: LABELS[next.key],
      atLocal: fmtHHMM(next.at),
      inSeconds: Math.max(0, Math.floor((next.epoch - now) / 1000)),
      dateGregorianISO: next.dateISO
    }
  };
}

export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}
